import type { ProfilePermissions, ReadOnlyProfilePermissions } from "@qendo/permissions";
import { type ContactableProfileRole, DefaultPermissionsMap, ProfileRole } from "@qendo/profile";
import type { ContactData, ContactPayload } from "@qendo/profile/contacts";
import type { InvitationPayload, ProfileInvitationData } from "@qendo/profile/invitation";
import type { TeamMemberData, TeamMemberUpdatePayload } from "@qendo/profile/team";
import { OptionType } from "@qendo/types";
import { useEffect, useId, useState } from "react";
import { Button } from "~/components/Button";
import { Badge } from "~/components/feedback/Badge";
import { CheckboxInput } from "~/components/form/CheckboxInput";
import { OptionSelector } from "~/components/form/combo-box/OptionSelector";
import { FieldGroup } from "~/components/form/FieldGroup";
import { TextInput } from "~/components/form/TextInput";
import { PermissionEditor } from "~/components/form/team/PermissionEditor";
import { SelectFromContacts } from "~/components/form/team/SelectFromContacts";
import { TeamRoleSelector } from "~/components/form/team/TeamRoleSelector";
import { FullPageModal, type FullPageModalProps } from "~/components/layout/modal/FullPageModal";
import { Only } from "~/components/permission/Only";
import { Permissions } from "~/entities/profile/permissions";
import { useErrorBag } from "~/providers/ErrorBagProvider";
import { useFeedback } from "~/providers/FeedbackProvider";
import { useProfile } from "~/providers/ProfileProvider";
import { empty, filled, isEmail } from "~/util";
import { isTeamMember, isUserBackedMember } from "~/util/qendo/team";

export type TeamMemberFormProps = {
    readonly member?: ProfileInvitationData | TeamMemberData | ContactData | null;
    onRequestClose: () => void;
} & FullPageModalProps;

type UpdatePayload = ContactPayload | TeamMemberUpdatePayload | InvitationPayload;
type UpdateMethod<T extends UpdatePayload> = (payload: T) => Promise<unknown>;

type TeamUpdateTuple<T extends UpdatePayload> = [UpdateMethod<T>, T, string];

export const TeamMemberForm = ({ member, onRequestClose, ...modalProps }: TeamMemberFormProps) => {
    const { canUpdateRole, storeInvitation, storeContact, updateTeamMember } = useProfile();
    const errorBag = useErrorBag();
    const { saving, setSaving, addToast } = useFeedback();
    const [role, setRole] = useState<ProfileRole | null>(member?.role ?? null);
    const [email, setEmail] = useState<string>(member?.email ?? "");
    const [phone, setPhone] = useState<string>(member?.phone ?? "");
    const [name, setName] = useState<string>(member?.name ?? "");
    const [contact, setContact] = useState<boolean>(member?.emergency_contact ?? false);
    const [resend, setResend] = useState<boolean>(false);
    const [consented, setConsented] = useState<boolean>(false);
    const [option, setOption] = useState<string | null>(member?.option_id ?? null);
    const [dirty, setDirty] = useState(false);
    const [permissions, setPermissions] = useState<ProfilePermissions>(
        new Permissions((member as TeamMemberData)?.permissions),
    );
    const id = useId();

    // biome-ignore lint/correctness/useExhaustiveDependencies(errorBag.forget): shared value
    useEffect(() => {
        errorBag.forget(["email", "phone", "name", "consent_granted", "permissions", "role"]);

        setEmail(member?.email ?? (member as TeamMemberData)?.user?.email ?? "");
        setPhone(member?.phone ?? (member as TeamMemberData)?.user?.phone ?? "");
        setName(member?.name ?? (member as TeamMemberData)?.user?.name ?? "");
        setContact(member?.emergency_contact ?? false);
        setRole(member?.role ?? null);
        setDirty(false);
        setPermissions(new Permissions((member as TeamMemberData)?.permissions));
        setOption(member?.option_id ?? null);
    }, [member]);

    useEffect(() => {
        setDirty(
            (isDirty) =>
                isDirty ||
                (member?.email ?? "") !== email ||
                (member?.phone ?? "") !== phone ||
                (member?.name ?? "") !== name ||
                (member?.option_id ?? null) !== option ||
                (role === ProfileRole.share &&
                    !new Permissions(permissions).matchesPermissions(
                        (member as TeamMemberData)?.permissions ?? DefaultPermissionsMap[role],
                    )),
        );
    }, [member, email, phone, name, permissions, role, option]);

    const creating = empty(member?.id);
    const editing = !creating;
    const is_user = isUserBackedMember(member);
    const is_contact = role === ProfileRole.contact;
    const is_share = role === ProfileRole.share;
    const can_see_role = creating || (!is_contact && !is_share);
    const can_set_role = can_see_role && canUpdateRole(role);
    const is_real_user = isTeamMember(member);
    const is_invite = !is_contact && !is_user;
    const user_details_changed =
        (member?.email ?? "") !== email || (member?.phone ?? "") !== phone || (member?.name ?? "") !== name;
    const must_consent = creating || (user_details_changed && !is_share);

    useEffect(() => {
        setConsented(filled(member?.id) && !must_consent);
    }, [member, must_consent]);

    const can_save =
        !saving &&
        canUpdateRole(role) &&
        (isEmail(email) || (is_contact && empty(email)) || (is_share && is_user)) &&
        filled(name) &&
        filled(role) &&
        (!must_consent || consented) &&
        (!is_share || filled(permissions));

    const sendInvite = () => {
        if (!can_save) {
            return;
        }

        setSaving(id);
    };

    const commit = () => {
        const base_payload = {
            ...(filled(member?.id) ? { id: member.id } : {}),
            name,
            role,
            ...(is_share
                ? { permissions: new Permissions(permissions).onlySet() }
                : {
                      email,
                      phone,
                      emergency_contact: contact,
                      consent_granted: consented,
                      option_id: option,
                  }),
        };

        const [action, payload, content] = ((is_contact &&
            ([
                storeContact,
                base_payload,
                `Contact ${creating ? "added" : "updated"}`,
            ] as TeamUpdateTuple<ContactPayload>)) ||
            (is_user &&
                ([
                    updateTeamMember,
                    base_payload,
                    is_share ? "Share details updated." : "Team member updated.",
                ] as TeamUpdateTuple<TeamMemberUpdatePayload>)) ||
            ([
                storeInvitation,
                {
                    ...base_payload,
                    resend_invite: creating ? undefined : resend,
                },
                creating || resend ? `Invitation sent to ${name}` : "Invitation updated.",
            ] as TeamUpdateTuple<InvitationPayload>)) as TeamUpdateTuple<ContactPayload>;

        action(payload)
            .then(() => {
                addToast({ content });

                onRequestClose();
            })
            .finally(() => setSaving(false));
    };

    const is_saving = saving === id;

    // biome-ignore lint/correctness/useExhaustiveDependencies(commit): callback
    useEffect(() => {
        is_saving && commit();
    }, [is_saving]);

    const method = creating ? (is_contact ? "Create" : "Invite") : "Update";
    const target = (is_contact && "contact") || (is_share && "share") || "care team";
    const button_text =
        (is_contact && "Save contact") ||
        (is_share && "Update share") ||
        (is_user && "Update role") ||
        (!creating && !resend && "Update invite") ||
        "Send invite";

    return (
        <FullPageModal
            {...modalProps}
            dirty={dirty}
            onRequestClose={onRequestClose}
            title={`${method} ${target}`}
            cancelable
            headerRight={() => (
                <Button variant="secondary" title={button_text} size="sm" disabled={!can_save} onPress={sendInvite} />
            )}
        >
            <FieldGroup>
                <TextInput
                    label="Name"
                    name="name"
                    value={name}
                    onChangeText={setName}
                    right={
                        creating
                            ? () => (
                                  <SelectFromContacts
                                      disabled={is_real_user}
                                      setName={setName}
                                      setEmail={setEmail}
                                      setPhone={setPhone}
                                  />
                              )
                            : undefined
                    }
                />
                {!is_share && (
                    <>
                        <TextInput
                            label="Email"
                            name="email"
                            value={email}
                            onChangeText={setEmail}
                            autoCapitalize={"none"}
                            textContentType="emailAddress"
                            optional={is_contact}
                        />
                        <TextInput
                            label="Phone"
                            name="phone"
                            optional
                            value={phone}
                            onChangeText={setPhone}
                            textContentType="telephoneNumber"
                        />
                    </>
                )}
                <Only when={!is_user || must_consent}>
                    <CheckboxInput
                        label="This person gave consent to be added to my care team"
                        value={consented}
                        onValueChange={setConsented}
                        name="consent_granted"
                        required
                    />
                </Only>
                {is_real_user && user_details_changed && (
                    <Badge
                        variant="info"
                        title="This will only affect how this team member appears in your care team and contacts."
                        icon="info"
                    />
                )}
                {can_see_role && (
                    <TeamRoleSelector
                        name="role"
                        value={role as ContactableProfileRole | null}
                        onValueChange={setRole}
                        withContact={creating}
                        disabled={!can_set_role}
                    />
                )}
                <Only not when={is_share}>
                    <OptionSelector
                        name="option_id"
                        label="Relationship"
                        type={OptionType.team_members}
                        value={option}
                        onItemSelected={setOption}
                        optional
                        clearable
                    />
                    <CheckboxInput label="Emergency Contact" value={contact} onValueChange={setContact} />
                </Only>
                <Only when={is_share}>
                    <PermissionEditor
                        title="Content to share"
                        value={permissions as ReadOnlyProfilePermissions}
                        onChangePermissions={setPermissions}
                        role={ProfileRole.share}
                        editing
                    />
                </Only>
                <Only when={editing && is_invite}>
                    <CheckboxInput
                        label="Re-send invitation email"
                        value={resend}
                        onValueChange={setResend}
                        name="resend_invite"
                    />
                </Only>
            </FieldGroup>
        </FullPageModal>
    );
};
